/**
 * Retrieves the current token item from the provided authentication repository.
 *
 * @param repository - The authentication repository from which to retrieve the token item.
 * @returns The current token item.
 */
const getCurrentTokenItem = repository => {
  return repository.getTokenItem();
};

/**
 * Logs out the user by removing the authentication token.
 *
 * @param repository - The authentication repository that provides the method to remove the token.
 * @returns A promise that resolves when the token has been removed.
 */
const logout = repository => {
  return repository.removeTokenItem();
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const SAFE_TIME = 30000;
/**
 * Sets the token item in the repository.
 *
 * @param tokenItem - The token item to be set. It must not be null and must contain a refresh token.
 * @param repository - The repository where the token item will be saved.
 * @throws Will throw an error if the token item is null or does not contain a refresh token.
 */
const setTokenItem = (repository, tokenItem) => {
  if (!tokenItem || !tokenItem.refresh) {
    throw new Error('Token item cannot be null');
  }
  if (tokenItem.expirationTime && typeof tokenItem.expirationTime === 'number') tokenItem.expirationTime = tokenItem.expirationTime - SAFE_TIME;
  repository.saveTokenItem(tokenItem);
};

/**
 * Refreshes the current authentication token using the provided repository.
 *
 * @param repository - The repository instance that provides methods to get, refresh, and save token items.
 * @returns A promise that resolves to a boolean indicating whether the token was successfully refreshed.
 */
const refreshToken = repository => __awaiter(void 0, void 0, void 0, function* () {
  const currentTokenItem = repository.getTokenItem();
  if (!currentTokenItem) return false;
  const tokenItemResponse = yield repository.refreshTokenItem();
  if (!tokenItemResponse) return false;
  setTokenItem(repository, tokenItemResponse);
  return true;
});

const setVisitantMode = (repository, visitandMode) => {
  return repository.setVisitantMode(visitandMode);
};

export { SAFE_TIME, getCurrentTokenItem, logout, refreshToken, setTokenItem, setVisitantMode };
